import { MutableRefObject, useEffect, useRef } from "react";

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  /** Number of items rendered, so a still-visible sentinel can trigger again. */
  itemCount?: number;
  onLoadMore: () => void;
  rootMargin?: string;
}

interface UseInfiniteScrollResult {
  loadMoreRef: MutableRefObject<HTMLDivElement | null>;
}

/**
 * Observes a sentinel element rendered at the end of a list and calls
 * onLoadMore when it scrolls into view. Render the sentinel only while
 * hasMore is true.
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  itemCount,
  onLoadMore,
  rootMargin = "200px",
}: UseInfiniteScrollOptions): UseInfiniteScrollResult => {
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const wasIntersectingRef = useRef(false);

  // Allow the sentinel to trigger again once a load settles, so a sentinel
  // that is still visible after appending keeps filling the viewport.
  useEffect(() => {
    if (isLoading) return;
    wasIntersectingRef.current = false;
  }, [isLoading, itemCount]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (!hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = Boolean(entries[0]?.isIntersecting);
        if (isIntersecting && !wasIntersectingRef.current) {
          onLoadMore();
        }
        wasIntersectingRef.current = isIntersecting;
      },
      { root: null, rootMargin, threshold: 0 },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, itemCount, onLoadMore, rootMargin]);

  return { loadMoreRef };
};
